/**
 * Shared focus-ring utilities.
 *
 * Two mechanisms, and only two:
 *
 *   1. The global `:focus-visible` outline in `index.css`. This is the
 *      default for everything - links, inputs, table rows - and needs no
 *      opt-in. 2px solid `--murmuration-focus-ring` at 2px offset.
 *   2. A Tailwind *ring*, via the constants below, for controls that need
 *      the indicator painted as a ring (so it follows `rounded-*` and can
 *      carry its own offset colour). These suppress the outline so the two
 *      indicators never double up.
 *
 * Pick one per element. Setting a ring class and then expecting the
 * outline rules to apply to the same element does not work.
 *
 * ## Why there is no separate "on brand" class
 *
 * `FOCUS_RING_CLASS` already works on brand-filled buttons. Its 2px offset
 * is white, so the indicator reads: teal fill, white gap (4.74:1 against
 * the fill), blue ring (6.70:1 against the gap, 6.19:1 against the page
 * background). Every edge clears the WCAG 2.2 SC 1.4.11 3:1 floor.
 *
 * An earlier `data-on-brand="true"` attribute claimed to invert the ring to
 * white on brand fills. It never did anything - `FOCUS_RING_CLASS` sets
 * `outline-none`, and the attribute only recoloured the outline. It has
 * been removed rather than repaired, because repairing it would have made
 * things worse: a white indicator is 1.08:1 against the `#F4F6F8` page
 * background, so its outer edge would have been invisible.
 */

/**
 * Ring for controls on light surfaces, including brand-filled buttons.
 * White offset separates the ring from whatever sits underneath.
 */
export const FOCUS_RING_CLASS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-focus-ring'

/**
 * Ring for controls sitting on the dark sidebar.
 *
 * The default ring colour `#1D4ED8` is only 2.5:1 against `--side`
 * (`#0F1F34`) - under the 3:1 non-text floor. White is 15.6:1 there. The
 * offset is drawn in the sidebar colour so the ring reads as a clean
 * outline rather than a light halo.
 */
export const FOCUS_RING_ON_DARK_CLASS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-side'
